/**
 * Recent Referrals
 * Shows list of recent referrals with status badges
 */

import { Users, UserPlus } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { getStatusBadgeStyle, formatDate } from '../utils/helpers';
import type { Referral } from '../types';

interface RecentReferralsProps {
  referrals: Referral[];
  maxItems?: number;
}

function ReferralItem({ referral }: { referral: Referral }) {
  const { t } = useLanguage();
  const statusStyle = getStatusBadgeStyle(referral.status);
  const isQualified = referral.status === 'qualified' || referral.status === 'rewarded';

  const statusLabels = {
    pending: t('referrals.statusPending', 'Pending'),
    qualified: t('referrals.statusQualified', 'Qualified'),
    rewarded: t('referrals.statusRewarded', 'Rewarded'),
  };

  return (
    <div className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isQualified ? 'bg-green-100' : 'bg-amber-100'
          }`}
        >
          <Users className={`w-5 h-5 ${statusStyle.icon}`} />
        </div>
        <div>
          <div className="font-medium text-gray-900">
            {t('referrals.referralNumber', 'Referral #')}
            {referral.id.slice(0, 8)}
          </div>
          <div className="text-sm text-gray-500">
            {t('referrals.joined', 'Joined')} {formatDate(referral.createdAt)}
          </div>
        </div>
      </div>
      <div className={`px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text}`}>
        {statusLabels[referral.status]}
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useLanguage();

  return (
    <div className="px-6 py-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <UserPlus className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">
        {t('referrals.noReferralsYet', 'No referrals yet')}
      </h3>
      <p className="text-sm text-gray-500 max-w-xs mx-auto">
        {t(
          'referrals.inviteFriendsPrompt',
          'Share your referral code with friends and family to see them here!'
        )}
      </p>
    </div>
  );
}

export function RecentReferrals({ referrals, maxItems = 5 }: RecentReferralsProps) {
  const { t } = useLanguage();
  const displayedReferrals = referrals.slice(0, maxItems);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('referrals.recentReferrals', 'Recent Referrals')}
        </h2>
        {referrals.length > 0 && (
          <p className="text-sm text-gray-500">
            {t('referrals.showingRecent', `Showing ${displayedReferrals.length} of ${referrals.length}`)}
          </p>
        )}
      </div>

      {referrals.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="divide-y divide-gray-100">
          {displayedReferrals.map((referral) => (
            <ReferralItem key={referral.id} referral={referral} />
          ))}
        </div>
      )}
    </div>
  );
}
